import { forwardRef, PropsWithChildren, ReactNode } from 'react';
import {
  Disclosure,
  DisclosureContent,
  DisclosureStateReturn,
} from 'reakit/Disclosure';
import { ArrowDown as ArrowIcon } from '@mos-cat/ds-icons';
import { pxToRem } from '@mos-cat/ds-theme';
import { Box } from '../Box/Box';
import { Heading } from '../Heading/Heading';
import { Text } from '../Text/Text';
import { focus } from '../utils/focusable';

export { useDisclosureState as useAccordionState } from 'reakit/Disclosure/DisclosureState';

type AccordionProps = {
  heading: ReactNode;
  headingLevel?: 2 | 3 | 4 | 5 | '2' | '3' | '4' | '5';
  showBottomBorder?: boolean;
} & DisclosureStateReturn;

export const Accordion = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<AccordionProps>
>(
  (
    {
      heading,
      headingLevel = 3,
      children,
      showBottomBorder = true,
      ...accordionState
    },
    ref
  ) => (
    <Box
      styles={{
        borderBottomWidth: showBottomBorder ? '1px' : 0,
        borderStyle: 'solid',
        borderColor: 'accordionColorBorder',
      }}
    >
      <Heading
        level={headingLevel}
        styles={{
          width: '100%',
          display: 'flex',
          color: 'inherit',
          textAlign: 'left',
          justifyContent: 'space-between',
        }}
      >
        <Box
          ref={ref}
          as={Disclosure}
          styles={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            color: 'accordionColorText',
            py: 'accordionPaddingY',

            ':focus': {
              outline: 'none',

              'div:first-child': {
                ...focus,
                color: 'accordionColorFocusText',
              },
            },

            ':active': {
              'div:first-child': {
                color: 'accordionColorActiveText',
              },
            },

            ':hover': {
              color: 'accordionColorHoverText',
            },

            '&[aria-expanded="true"] svg': {
              transform: 'rotate(180deg)',
            },
          }}
          {...accordionState}
        >
          <Box
            styles={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              borderRadius: 'accordion',
            }}
          >
            <Text
              styles={{
                fontWeight: 600,
                display: 'flex',
                textAlign: 'left',
                width: '100%',
                maxWidth: pxToRem(736),
                flexWrap: 'wrap',
              }}
            >
              {heading}
            </Text>
            <Box
              as={ArrowIcon}
              alignSelf="flex-start"
              styles={{
                marginLeft: pxToRem(16),
                width: '1.2em',
                marginTop:
                  Number(headingLevel) === 3 ? 'accordionIconMarginTop' : '0',
                minWidth: 'accordionMinWidth',
                height: 'auto',
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
      </Heading>

      <DisclosureContent
        as={Box}
        styles={{
          paddingBottom: 'accordionPaddingY',
          maxWidth: pxToRem(736),
        }}
        {...accordionState}
      >
        {children}
      </DisclosureContent>
    </Box>
  )
);
